import {useQuery} from '@apollo/client';
import {Empty, Spin} from 'antd';
import DBModal from 'components/modal/modal';
import {Button} from '@dabafinance/react-components';
import {ModalShell} from 'pages/adminOpportunities/stocksListings/ViewOrders/ui/ModalShell';
import {GET_INSTRUMENT_TRANSLATION_PREVIEW} from 'graphql/queries/assetManagement';
import styles from './translationReview.module.scss';

const LANGUAGE_LABELS = {EN: 'English', FR: 'French'};

/**
 * The prose fields a reviewer is being asked to sign off on, in the order they read.
 *
 * Deliberately a flat list rather than "whatever the instrument returned": the union brings
 * back ids and names too, and dumping every key would bury the two sentences that actually
 * changed. Fields absent on a given type simply never render.
 */
const PROSE_FIELDS = [
  ['description', 'Description'],
  ['investmentStrategy', 'Investment strategy'],
  ['thesisAndInvestmentPhilosophy', 'Thesis and investment philosophy'],
  ['bestSuitedFor', 'Best suited for'],
];

const LIST_FIELDS = [
  ['highlights', 'Highlights'],
  ['benefits', 'Benefits'],
];

const hasText = value => typeof value === 'string' && value.trim().length > 0;

const Section = ({title, children}) => (
  <section className={styles.previewSection}>
    <h4 className={styles.previewLabel}>{title}</h4>
    {children}
  </section>
);

/**
 * Reads one language of an instrument as mobile would render it.
 *
 * For an authenticated admin the gateway returns PENDING_REVIEW copy as well as approved
 * copy, which is what makes reviewing before publishing possible. Reading is a pure read —
 * it never changes the row's approval state.
 */
export const TranslationPreviewModal = ({
  instrumentId,
  language,
  openModal,
  setOpenModal,
  onApprove,
  approving,
  canApprove,
}) => {
  const {data, loading, error} = useQuery(GET_INSTRUMENT_TRANSLATION_PREVIEW, {
    variables: {id: instrumentId, language},
    // A draft that was just approved elsewhere must not be served from cache as pending.
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !openModal || !instrumentId || !language,
  });

  const result = data?.getFinancialInstrument;
  const instrument = result?.__typename === 'Error' ? null : result?.data;
  const message =
    result?.__typename === 'Error' ? result.message : error?.message;

  const keyFacts = (instrument?.keyFacts ?? []).filter(
    fact => hasText(fact?.name) || hasText(fact?.value),
  );
  const allocations = (
    instrument?.offering?.useOfProceeds?.allocations ?? []
  ).filter(item => hasText(item?.allocationCategory));
  const proceeds = instrument?.offering?.useOfProceeds?.description;

  const prose = PROSE_FIELDS.filter(([field]) => hasText(instrument?.[field]));
  const lists = LIST_FIELDS.map(([field, label]) => [
    label,
    (instrument?.[field] ?? []).filter(hasText),
  ]).filter(([, values]) => values.length > 0);

  const nothingToShow =
    !loading &&
    !message &&
    !prose.length &&
    !lists.length &&
    !keyFacts.length &&
    !hasText(proceeds) &&
    !allocations.length;

  return (
    <DBModal
      isOpen={openModal}
      handleClose={() => setOpenModal(false)}
      content={
        <ModalShell
          title={`${LANGUAGE_LABELS[language] ?? language} copy`}
          subtitle={
            instrument?.name
              ? `${instrument.name} — as users reading ${
                  LANGUAGE_LABELS[language] ?? language
                } would see it`
              : undefined
          }
          footer={
            <>
              <Button
                onClick={() => setOpenModal(false)}
                type="secondary"
                label="Close"
              />
              {canApprove && (
                <Button
                  disabled={approving || loading || !!message}
                  onClick={onApprove}
                  label={approving ? 'Approving…' : 'Approve and publish'}
                />
              )}
            </>
          }>
          {loading && (
            <div className={styles.previewLoading}>
              <Spin />
            </div>
          )}

          {!loading && message && (
            <p className={styles.previewError}>{message}</p>
          )}

          {nothingToShow && (
            <Empty description="No translated copy on this instrument yet." />
          )}

          {!loading &&
            !message &&
            prose.map(([field, label]) => (
              <Section key={field} title={label}>
                {/* Translated copy is stored as HTML — DeepL is called with the `html`
                    tag handler so markup survives — and rendering it as text would show
                    the reviewer tags rather than the copy they are approving. */}
                <div
                  className={styles.previewProse}
                  dangerouslySetInnerHTML={{__html: instrument[field]}}
                />
              </Section>
            ))}

          {!loading &&
            !message &&
            lists.map(([label, values]) => (
              <Section key={label} title={label}>
                <ul className={styles.previewList}>
                  {values.map((value, index) => (
                    <li key={`${label}-${index}`}>{value}</li>
                  ))}
                </ul>
              </Section>
            ))}

          {!loading && !message && keyFacts.length > 0 && (
            <Section title="Key facts">
              <dl className={styles.previewFacts}>
                {keyFacts.map((fact, index) => (
                  <div key={`fact-${index}`}>
                    <dt>{fact.name}</dt>
                    <dd>{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </Section>
          )}

          {!loading && !message && hasText(proceeds) && (
            <Section title="Use of proceeds">
              <div
                className={styles.previewProse}
                dangerouslySetInnerHTML={{__html: proceeds}}
              />
            </Section>
          )}

          {!loading && !message && allocations.length > 0 && (
            <Section title="Proceeds allocation">
              <dl className={styles.previewFacts}>
                {allocations.map((item, index) => (
                  <div key={`allocation-${index}`}>
                    <dt>{item.allocationCategory}</dt>
                    {/* Percentages are excluded from translation on purpose, so they are
                        shown here as confirmation that the numbers survived it. */}
                    <dd>
                      {item.percentageValue === null ||
                      item.percentageValue === undefined
                        ? '—'
                        : `${item.percentageValue}%`}
                    </dd>
                  </div>
                ))}
              </dl>
            </Section>
          )}
        </ModalShell>
      }
    />
  );
};
